// WHICH ROUTES MAY BE UNDER TEST, and the switch that puts one there.
//
// A PRERENDERED PAGE CANNOT CARRY A PER-VISITOR VARIANT. It is a file, written
// once at build time and served to everybody, so the only way to show one
// visitor a different version is to change it after it arrives — which is the
// flash of the original that makes a test measure the flash as much as the
// change. So a route under test is rendered per request for the duration, and
// goes back to being a file the moment the experiment ends.
//
// THAT IS WHY THIS IS A BUILD-TIME VALUE and not a runtime one. Whether a route
// is prerendered is decided while the site is built; a switch the running
// container could flip would be a switch that changes nothing, because the file
// already exists. Starting an experiment on a route therefore means a build,
// and stopping one means a build back. The list below is what makes that a
// one-word change rather than an edit to a page.
//
// THE ELIGIBLE SET IS CLOSED. A route not named here cannot be put under test
// at all, however the variable is set, and the test beside this module walks
// the app's routes to check that each eligible one actually exists. The reason
// is the cabinet: a page that renders a customer's end-user document fields
// must never be quietly flipped out of its rendering mode by an environment
// variable, and the safest way to guarantee that is a list that does not name
// it.
//
// Universal: this module is imported by a `+page.ts`, which runs in the browser
// as well as on the server, so it reads only what the bundler substitutes.

/**
 * Every route an experiment may be run on.
 *
 * Public, written prose with no form and no document on it — the same class the
 * session recorder is allowed on, for the same reason. They are also the
 * highest-traffic pages on the site, which is the only kind of page where an
 * experiment finishes in a useful time.
 */
export const ELIGIBLE_ROUTES: readonly string[] = ["/", "/pricing"];

/**
 * Every variant value a page under test is rendered with by the guard beside
 * this module.
 *
 * WHAT AN EXPERIMENT MAY NEVER CHANGE. A variant may move a headline, an
 * arrangement, an image, the order of sections. It may NOT change the price,
 * anything that states what a charge turns on, any published legal text or the
 * link to one, or any consent control. Two visitors shown two different prices
 * or two different agreements are being shown two different products, and no
 * measurement is worth that — the difference would also be invisible, because
 * the figure an experiment reports is a conversion rate and not what the page
 * said.
 *
 * The rule is held by `variant-copy.test.ts`, which renders every eligible
 * route with every value below and compares the price figures, the legal links
 * and the consent controls it finds. The list is what a page could actually be
 * handed: the two arms a page branches on, plus a value it does not recognise,
 * because the platform's answer is a free-form string and an arm added in the
 * dashboard reaches the page before any code here knows its name.
 */
export const PROVEN_VARIANTS: readonly string[] = ["control", "treatment", "an-unnamed-arm"];

/** The build-time variable naming the routes under test, comma-separated. */
export const ROUTES_ENV_NAME = "VITE_EXPERIMENT_ROUTES";

function configured(): readonly string[] {
  const raw = import.meta.env.VITE_EXPERIMENT_ROUTES;
  return parseRoutes(typeof raw === "string" ? raw : "");
}

/**
 * The routes a value names, dropping anything not on the eligible list.
 *
 * Dropped silently rather than refused, and that is the safe direction: a typo
 * leaves a page prerendered exactly as it was, while a build that failed over
 * one would take the site down for a measurement.
 */
export function parseRoutes(
  value: string,
  eligible: readonly string[] = ELIGIBLE_ROUTES,
): string[] {
  return value
    .split(",")
    .map((route) => route.trim())
    .filter((route) => eligible.includes(route));
}

/** Whether this build puts the given route under test. */
export function isUnderTest(route: string): boolean {
  return configured().includes(route);
}

/** Every route this build put under test, for the server-side assignment. */
export function routesUnderTest(): readonly string[] {
  return configured();
}

/**
 * The experiment's feature key for a route.
 *
 * Derived from the path rather than configured, so the name in the dashboard
 * and the route it changes cannot drift apart: `/pricing` is `page-pricing`,
 * `/` is `page-home`. An operator creating the feature types the name this
 * produces, and the deploy howto says so.
 */
export function featureKeyFor(route: string): string {
  const slug = route === "/" ? "home" : route.replace(/^\/+/, "").replace(/\//g, "-");
  return `page-${slug}`;
}
